import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Smartphone } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { errorText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FormError } from "./login/Fields";
import { StepTitle } from "./login/LoginShell";

// The QR for an authenticator app, with the key to type when the camera can't scan it.
export const QrKey = ({ setup, testid = "mfa-setup" }) => (
  <div className="flex items-center gap-4 rounded-2xl bg-white p-4 shadow-inner">
    {setup ? (
      <img data-testid={`${testid}-qr`} src={setup.qr} alt="QR code for your authenticator app" className="h-32 w-32 shrink-0" />
    ) : (
      <div className="grid h-32 w-32 shrink-0 place-items-center rounded-xl bg-[#f6f1e4]"><Loader2 className="h-6 w-6 animate-spin text-[#6b6b73]" /></div>
    )}
    <div className="min-w-0 text-xs text-[#5a554b]">
      <p className="flex items-center gap-1.5 font-semibold text-[#1f1f24]"><Smartphone className="h-3.5 w-3.5" /> Can't scan?</p>
      <p className="mt-1">Type this key instead:</p>
      <p data-testid={`${testid}-secret`} className="mt-1 break-all font-mono2 text-[13px] text-[#1f1f24]">{setup?.secret || "…"}</p>
    </div>
  </div>
);

// First-time 2-step setup: scan the QR (or type the key) in an authenticator app, then confirm with one code.
export const TwoFactorSetup = ({ onEnabled }) => {
  const { startMfaSetup, confirmMfa } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    startMfaSetup().then(setSetup).catch((err) => setError(errorText(err, "Couldn't start the setup. Please sign in again.")));
  }, [startMfaSetup]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      onEnabled(await confirmMfa(code.trim()));
    } catch (err) {
      setError(errorText(err, "That code didn't match."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mfa-setup-form">
      <StepTitle title="Turn on 2-step sign-in">
        Your account needs it. Scan this with an authenticator app (Google Authenticator, 1Password, Authy…).
      </StepTitle>
      <QrKey setup={setup} />
      <div className="space-y-1.5">
        <Label htmlFor="mfa-setup-code">The 6-digit code the app shows</Label>
        <Input
          id="mfa-setup-code"
          data-testid="mfa-setup-code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          className="h-12 rounded-full bg-white text-center font-mono2 text-xl tracking-[0.3em]"
        />
      </div>
      <FormError text={error} />
      <Button data-testid="mfa-setup-confirm-btn" type="submit" disabled={busy || !setup || code.length !== 6} className="h-12 w-full rounded-full font-bold gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Turn it on
      </Button>
    </form>
  );
};
